import { parseDecimalToNumber } from './decimals';
import { hasStringValue, hasValue } from './equality';

export enum ExportFormat {
  Xlsx = 'xlsx',
  Csv = 'csv',
}

export type ExportCell = string | number | null;

export type ExportSheet = {
  name: string;
  headers: string[];
  rows: ExportCell[][];
};

export type ExportColumn<TRow> = {
  header: string;
  getValue: (row: TRow) => ExportCell;
};

/** Parses a serialized decimal string into a real number cell, or null for a blank cell. */
export const toNumberOrNull = (value: string | null): number | null => {
  // Number('') is 0, and summaries default missing amounts to ''.
  if (!hasStringValue(value)) return null;
  const parsed = parseDecimalToNumber(value, NaN);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Builds an ExportSheet from a row-agnostic column list, one row per item in `rows`. */
export const sheetFromColumns = <TRow>(
  name: string,
  columns: ExportColumn<TRow>[],
  rows: TRow[],
): ExportSheet => ({
  name,
  headers: columns.map((column) => column.header),
  rows: rows.map((row) => columns.map((column) => column.getValue(row))),
});

const CSV_NEEDS_QUOTING = /[",\r\n]/;
// Spreadsheet apps evaluate these as formulas; the leading quote keeps them text (OWASP CSV injection).
// A lone trigger character can't form a formula, and "-" is a common placeholder value.
const CSV_FORMULA_TRIGGER = /^[=+\-@\t\r]./s;

const toCsvField = (cell: ExportCell): string => {
  if (!hasValue(cell)) return '';
  if (typeof cell === 'number') return String(cell);
  const text = CSV_FORMULA_TRIGGER.test(cell) ? `'${cell}` : cell;
  return CSV_NEEDS_QUOTING.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/** Serializes a sheet as RFC 4180 CSV. */
export const toCsv = (sheet: ExportSheet): string =>
  [sheet.headers, ...sheet.rows].map((row) => row.map(toCsvField).join(',')).join('\r\n');
